import { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { OilsProvider } from "@/contexts/OilsContext";
import { ProductsProvider } from "@/contexts/ProductsContext";
import { RealtimeSyncProvider } from "@/contexts/RealtimeSyncContext";
import { AchievementNotificationProvider } from "@/contexts/AchievementNotificationContext";
import { AppLayout } from "@/components/AppLayout";
import { AchievementNotification } from "@/components/cabinet/AchievementNotification";
import { ProtectedAdminRoute } from "./components/admin/ProtectedAdminRoute";
import { SEO, defaultOrganizationJsonLd } from "@/components/SEO";

// Lazy load pages for code-splitting
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Favoritos = lazy(() => import("./pages/Favoritos"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Admin = lazy(() => import("./pages/Admin"));
const MeuArmario = lazy(() => import("./pages/MeuArmario"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="guia-natural-theme">
        <OilsProvider>
          <ProductsProvider>
          <RealtimeSyncProvider>
            <AchievementNotificationProvider>
              <TooltipProvider>
                {/* SEO padrão para todas as páginas */}
                <SEO jsonLd={defaultOrganizationJsonLd} />
                <Toaster />
                <BrowserRouter>
                  <AppLayout>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/perfil" element={<Profile />} />
                        <Route path="/meu-armario" element={<MeuArmario />} />
                        <Route path="/favoritos" element={<Favoritos />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogArticle />} />
                        <Route
                          path="/admin"
                          element={
                            <ProtectedAdminRoute>
                              <Admin />
                            </ProtectedAdminRoute>
                          }
                        />
                        <Route path="/privacidade" element={<Privacidade />} />
                        <Route path="/termos" element={<Termos />} />
                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </AppLayout>
                  <AchievementNotification />
                </BrowserRouter>
              </TooltipProvider>
            </AchievementNotificationProvider>
          </RealtimeSyncProvider>
          </ProductsProvider>
        </OilsProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
